import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Search, FileText, Loader2, Clock, MessageSquarePlus, Crown, Plus, X, Star, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useWorkspaceState } from '@/hooks/useWorkspaceState';
import { TaskShortcuts } from './TaskShortcuts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AVAILABLE_TASKS, TASK_CATEGORY_INFO, TASK_TO_CATEGORY, TASK_CATEGORIES, Task } from '@/types/taskTypes';
import { UniversalTaskPanel } from './UniversalTaskPanel';
import { taskApi, userApi, documentApi, SessionDocument } from '@/services/api';
import { useNotificationStore, useSessionStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { useAuth } from '@/context/AuthContext';
import { useTaskResultCopy } from '@/hooks/useTaskResultCopy';
import type { TaskResultAttachment } from '@/stores/sessionStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import PartsListDisplay from './task-displays/PartsListDisplay';
import SpecMapDisplay from './task-displays/SpecMapDisplay';
import ClaimsSupportDisplay from './task-displays/ClaimsSupportDisplay';
import ClaimChartDisplay from './task-displays/ClaimChartDisplay';
import ClaimsWithNumeralsDisplay from './task-displays/ClaimsWithNumeralsDisplay';
import CustomAnalysisDisplay from './task-displays/CustomAnalysisDisplay';
import IRMetadataDisplay from './task-displays/IRMetadataDisplay';
import IRInnovationAnalysisDisplay from './task-displays/IRInnovationAnalysisDisplay';
import IRTechClassificationDisplay from './task-displays/IRTechClassificationDisplay';
import DraftSummaryDisplay from './task-displays/DraftSummaryDisplay';
import DraftDetailedDescriptionDisplay from './task-displays/DraftDetailedDescriptionDisplay';
import DraftBackgroundDisplay from './task-displays/DraftBackgroundDisplay';
import DraftClaimsDisplay from './task-displays/DraftClaimsDisplay';
import OtherEmbodimentsDisplay from './task-displays/OtherEmbodimentsDisplay';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import TranslateDocumentDisplay from './task-displays/TranslateDocumentDisplay';
import LaTeXConvertDisplay from './task-displays/LaTeXConvertDisplay';
import OCRPdfDisplay from './task-displays/OCRPdfDisplay';
import AntecedentBasisDisplay from './task-displays/AntecedentBasisDisplay';
import TechnicalOutlineDisplay from './task-displays/TechnicalOutlineDisplay';
import PartsProcessOutlineDisplay from './task-displays/PartsProcessOutlineDisplay';
import { OAEmailResultView } from './task-displays/OAEmailResultView';
import MultiDocAnalysisDisplay from './task-displays/MultiDocAnalysisDisplay';

// ─── Multi-Doc Q&A helpers (shared with MultiDocQAPanel) ────────────────────

const PATENT_TYPES_SET = new Set(['patent', 'publication', 'foreign_patent']);

function parseResourceId(resourceId: string): { id: string; type: string } | null {
  if (resourceId.startsWith('foreign_patent-')) return { type: 'foreign_patent', id: resourceId.slice('foreign_patent-'.length) };
  if (resourceId.startsWith('patent-')) return { type: 'patent', id: resourceId.slice('patent-'.length) };
  if (resourceId.startsWith('publication-')) return { type: 'publication', id: resourceId.slice('publication-'.length) };
  return null;
}

function fmtResourceId(resourceId: string): string {
  if (!resourceId) return '';
  if (resourceId.startsWith('patent-')) return `US Patent ${resourceId.slice('patent-'.length)}`;
  if (resourceId.startsWith('publication-')) return `US Publication ${resourceId.slice('publication-'.length)}`;
  if (resourceId.startsWith('foreign_patent-')) return resourceId.slice('foreign_patent-'.length);
  return resourceId.replace('-', ' ');
}

function classifyPatentNum(raw: string): { cleanedNumber: string; docType: string; displayLabel: string } | null {
  if (!raw?.trim()) return null;
  let input = raw.trim();
  const foreignMatch = input.match(/^([A-Z]{2,3})/i);
  if (foreignMatch && foreignMatch[1].toUpperCase() !== 'US') {
    const cleaned = input.replace(/,/g, '').replace(/\s/g, '').toUpperCase();
    return { cleanedNumber: cleaned, docType: 'foreign_patent', displayLabel: `Foreign Patent (${foreignMatch[1].toUpperCase()}) (${cleaned})` };
  }
  let cleaned = input.replace(/,/g, '').replace(/\//g, '').replace(/\s/g, '').replace(/^US/i, '').replace(/[A-Z]\d*$/i, '');
  if (!/^\d+$/.test(cleaned)) return null;
  if ((cleaned.length === 10 || cleaned.length === 11) && /^2\d{3}/.test(cleaned)) {
    if (cleaned.length === 10) cleaned = cleaned.slice(0, 4) + '0' + cleaned.slice(4);
    return { cleanedNumber: cleaned, docType: 'publication', displayLabel: `US Publication (${cleaned})` };
  }
  const numVal = parseInt(cleaned, 10);
  if (cleaned.length === 8 && numVal >= 14000000) return null;
  if ((cleaned.length === 7 || cleaned.length === 8) && numVal < 14000000)
    return { cleanedNumber: cleaned, docType: 'patent', displayLabel: `US Patent (${cleaned})` };
  return null;
}

// Inline Multi-Doc Q&A panel for the Tasks page (no Dialog wrapper)
function MultiDocQATaskPanel({ onJobComplete }: { onJobComplete?: (jobId: string) => void }) {
  const { startPolling } = useJobPollingStore();
  const [sessionDocuments, setSessionDocuments] = useState<SessionDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);

  useEffect(() => {
    documentApi.fetchSessionDocuments().then(docs => {
      setSessionDocuments(docs);
    }).catch(() => {}).finally(() => setDocsLoading(false));
  }, []);

  const patentDocs = sessionDocuments.filter(doc => PATENT_TYPES_SET.has(doc.file_type || ''));

  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [manualInput, setManualInput] = useState('');
  const [manualDocs, setManualDocs] = useState<{ id: string; type: string; label: string }[]>([]);
  const [manualInputError, setManualInputError] = useState('');
  const [question, setQuestion] = useState('');
  const [emailResults, setEmailResults] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [jobStatus, setJobStatus] = useState<'idle' | 'submitted' | 'done' | 'error'>('idle');
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (jobStatus === 'submitted') {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [jobStatus]);

  useEffect(() => {
    if (!submittedJobId) return;
    const handler = (e: Event) => {
      const { jobId, success } = (e as CustomEvent).detail;
      if (jobId !== submittedJobId) return;
      setJobStatus(success ? 'done' : 'error');
      if (!success) setErrorMessage('Analysis failed. Please try again.');
      if (success) onJobComplete?.(jobId);
    };
    window.addEventListener('job-complete', handler as EventListener);
    return () => window.removeEventListener('job-complete', handler as EventListener);
  }, [submittedJobId, onJobComplete]);

  const toggleDoc = (id: string) => setSelectedDocIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleAddManual = () => {
    setManualInputError('');
    const entries = manualInput.split(/[\n;]+/).map(s => s.trim()).filter(Boolean);
    if (!entries.length) return;

    const failed: string[] = [];
    const toAdd: { id: string; type: string; label: string }[] = [];

    for (const entry of entries) {
      const classified = classifyPatentNum(entry);
      if (!classified) { failed.push(entry); continue; }
      const alreadyExists =
        manualDocs.some(d => d.id === classified.cleanedNumber && d.type === classified.docType) ||
        toAdd.some(d => d.id === classified.cleanedNumber && d.type === classified.docType);
      if (!alreadyExists) toAdd.push({ id: classified.cleanedNumber, type: classified.docType, label: classified.displayLabel });
    }

    if (toAdd.length) setManualDocs(prev => [...prev, ...toAdd]);
    if (failed.length) {
      setManualInputError(`Could not recognize: ${failed.join(', ')}`);
    } else {
      setManualInput('');
    }
  };

  const totalCount = selectedDocIds.size + manualDocs.length;
  const canSubmit = totalCount > 0 && question.trim().length > 0 && !submitting && jobStatus !== 'submitted';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const resources: { id: string; type: string }[] = [];
      for (const rid of selectedDocIds) { const p = parseResourceId(rid); if (p) resources.push(p); }
      for (const doc of manualDocs) resources.push({ id: doc.id, type: doc.type });
      const result = await taskApi.submitBulkAskTask(resources, question.trim(), emailResults) as any;
      const jobId = result?.job_id;
      if (!jobId) throw new Error('No job ID returned from server');
      const docLabel = resources.slice(0, 3).map(r => r.id).join(', ') + (resources.length > 3 ? ` +${resources.length - 3} more` : '');
      startPolling(jobId, 'multi_doc_analysis', docLabel);
      setSubmittedJobId(jobId);
      setJobStatus('submitted');
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to submit analysis. Please try again.');
      setJobStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => { setJobStatus('idle'); setErrorMessage(''); setElapsedSeconds(0); setSubmittedJobId(null); };

  return (
    <div className="flex flex-col gap-5 p-6 max-w-2xl">
      {/* Select from session docs */}
      <div>
        <div className="text-sm font-medium text-gray-800 mb-2">Select from loaded documents</div>
        {docsLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2"><Loader2 className="w-4 h-4 animate-spin" />Loading documents…</div>
        ) : patentDocs.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No patent documents in session. Add documents first or enter numbers below.</p>
        ) : (
          <div className="overflow-y-auto max-h-[220px] border border-gray-200 rounded-md">
            <div className="p-2 space-y-1">
              {patentDocs.map((doc: any) => (
                <label key={doc.resource_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                  <Checkbox checked={selectedDocIds.has(doc.resource_id)} onCheckedChange={() => toggleDoc(doc.resource_id)} />
                  <span className="text-sm text-gray-800 truncate">
                    {fmtResourceId(doc.resource_id)}
                    {doc.title && doc.title !== fmtResourceId(doc.resource_id) && (
                      <span className="text-gray-500 ml-1.5 text-xs">— {doc.title}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add by patent number */}
      <div>
        <div className="text-sm font-medium text-gray-800 mb-2">Add by patent number</div>
        <div className="flex gap-2">
          <Textarea value={manualInput} onChange={e => { setManualInput(e.target.value); setManualInputError(''); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddManual(); } }}
            placeholder={"e.g. US11941156\nEP3123456\n20230123456"}
            rows={3} className="flex-1 resize-none text-sm" />
          <Button variant="outline" onClick={handleAddManual} className="gap-1.5 shrink-0 self-start"><Plus className="w-4 h-4" />Add</Button>
        </div>
        {manualInputError && <p className="text-xs text-red-600 mt-1">{manualInputError}</p>}
        {manualDocs.length > 0 && (
          <div className="mt-2 space-y-1">
            {manualDocs.map((doc, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1 rounded bg-gray-50 border border-gray-200">
                <span className="text-sm text-gray-800">{doc.label}</span>
                <button onClick={() => setManualDocs(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-gray-700 ml-2">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Question */}
      <div>
        <div className="text-sm font-medium text-gray-800 mb-2">Question</div>
        <Textarea value={question} onChange={e => setQuestion(e.target.value)}
          placeholder="Ask a question about these documents…" rows={3} className="resize-none" />
      </div>

      {/* Submit / Status */}
      <div className="border-t border-gray-200 pt-4">
        {jobStatus === 'idle' || jobStatus === 'error' ? (
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Checkbox checked={emailResults} onCheckedChange={v => setEmailResults(!!v)} />
              <span className="text-sm text-gray-700">Email results to me when complete</span>
            </label>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-600">
              {totalCount > 0 ? `${totalCount} document${totalCount !== 1 ? 's' : ''} selected` : 'No documents selected'}
            </span>
            <div className="flex items-center gap-2">
              {jobStatus === 'error' && <Button variant="outline" size="sm" onClick={handleReset}>Reset</Button>}
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Run Analysis
              </Button>
            </div>
          </div>
          </div>
        ) : jobStatus === 'done' ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-green-700 font-medium">Analysis complete — see results in the history panel.</span>
            <Button variant="outline" size="sm" onClick={handleReset}>New Analysis</Button>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600 shrink-0" />
              <span>Processing in background… ({elapsedSeconds}s)</span>
            </div>
            <p className="text-xs text-gray-500">You can navigate away. A notification will appear when done.</p>
          </div>
        )}
        {jobStatus === 'error' && errorMessage && <p className="text-xs text-red-600 mt-2">{errorMessage}</p>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface TasksPageProps {
  preferredDocument?: any;
  openJobId?: string | null;
  onOpenJobIdConsumed?: () => void;
  openTaskId?: string | null;
  onOpenTaskIdConsumed?: () => void;
}

export function TasksPage({ openJobId, onOpenJobIdConsumed, openTaskId, onOpenTaskIdConsumed, preferredDocument }: TasksPageProps = {}) {
  const [selectedCategory, setSelectedCategory] = useWorkspaceState('tasks:category', 'all', value => typeof value === 'string' && !!TASK_CATEGORY_INFO[value]);
  const [searchTerm, setSearchTerm] = useWorkspaceState('tasks:search', '');
  const [selectedTaskId, setSelectedTaskId] = useWorkspaceState('tasks:selected', '');
  const selectedTask = AVAILABLE_TASKS.find(task => task.id === selectedTaskId && !task.hidden && !task.coming_soon) || null;
  const setSelectedTask = useCallback((task: Task | null) => setSelectedTaskId(task?.id || ''), [setSelectedTaskId]);
  const validTaskIds = (value: unknown) => Array.isArray(value) && value.every(taskId => typeof taskId === 'string');
  const [favoriteTaskIds, setFavoriteTaskIds] = useWorkspaceState<string[]>('tasks:favorites', [], validTaskIds, 'local');
  const [recentTaskIds, setRecentTaskIds] = useWorkspaceState<string[]>('tasks:recent', [], validTaskIds, 'local');
  const toggleFavorite = (taskId: string) => setFavoriteTaskIds(previous => previous.includes(taskId) ? previous.filter(id => id !== taskId) : [...previous, taskId]);
  useEffect(() => {
    if (selectedTaskId) setRecentTaskIds(previous => previous[0] === selectedTaskId ? previous : [selectedTaskId, ...previous.filter(id => id !== selectedTaskId)].slice(0, 12));
  }, [selectedTaskId, setRecentTaskIds]);
  const [showTaskList, setShowTaskList] = useState(true);
  const [allTaskHistory, setAllTaskHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const { addNotification } = useNotificationStore();
  const { showToast, copyResult } = useTaskResultCopy();
  const { user } = useAuth();
  const addTaskAttachment = useSessionStore(state => state.addTaskAttachment);
  const [showTaskInfoModal, setShowTaskInfoModal] = useState(false);
  const [historyReloadKey, setHistoryReloadKey] = useState(0);

  // Categorize tasks
  const categorizedTasks = useMemo(() => {
    const categories: Record<string, { name: string; icon: any; tasks: any[] }> = {};

    Object.entries(TASK_CATEGORY_INFO).forEach(([key, info]) => {
      // Skip form_processing category
      if (key === 'form_processing') return;
      categories[key] = { name: info.name, icon: info.icon, tasks: [] };
    });

    AVAILABLE_TASKS.forEach(task => {
      if (task.hidden || task.coming_soon) return;
      const category = task.task_type ? TASK_TO_CATEGORY[task.id] : 'legacy';
      const categoryKey = category || 'legacy';

      // Skip form_processing tasks
      if (categoryKey === 'form_processing') return;

      if (categories[categoryKey]) {
        categories[categoryKey].tasks.push(task);
      }
      categories.all.tasks.push(task);
    });

    return categories;
  }, []);

  // Fetch all task history when component mounts or when selectedTask changes
  useEffect(() => {
    const loadAllTaskHistory = async () => {
      if (selectedTask && selectedTask.id !== 'multi_doc_analysis' && !openJobId) {
        // Don't load all history when a task is selected (except multi_doc_analysis which needs its own history)
        return;
      }

      setHistoryLoading(true);
      try {
        // Get all task history (no filter)
        const response: any = await taskApi.getTaskHistory();
        const allHistory = response.history || [];

        // Filter out tasks that have dedicated history elsewhere
        const filteredHistory = allHistory.filter((item: any) => {
          if (item.task_type === 'custom_question') return false;
          // Exclude all form_processing tasks (USPTO Forms page has its own history)
          if (TASK_TO_CATEGORY[item.task_type] === TASK_CATEGORIES.FORM_PROCESSING) return false;
          if (item.task_type?.endsWith('_pdf_generation') || item.task_type?.startsWith('form_')) return false;
          return true;
        });

        // Sort by newest first
        const sortedHistory = filteredHistory.sort((a: any, b: any) => {
          const dateA = new Date(a.time_received || a.timestamp).getTime();
          const dateB = new Date(b.time_received || b.timestamp).getTime();
          return dateB - dateA;
        });

        setAllTaskHistory(sortedHistory);
      } catch (error) {
        console.error('Failed to load all task history:', error);
        setAllTaskHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadAllTaskHistory();
  }, [selectedTask, historyReloadKey, openJobId]);

  // Fetch account info when component mounts
  useEffect(() => {
    const fetchAccountInfo = async () => {
      if (!user) return;
      try {
        const response = await userApi.fetchAccountInfo();
        if (response && !response.error) {
          setAccountInfo(response);
        }
      } catch (error) {
        console.error('Failed to fetch account info:', error);
      }
    };
    fetchAccountInfo();
  }, [user]);

  // Listen for task selection from wizard
  useEffect(() => {
    const handleSelectTask = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { taskId } = customEvent.detail;
      console.log('[TasksPage] Received select-task event for:', taskId);

      const task = AVAILABLE_TASKS.find(t => t.id === taskId);
      if (task) {
        console.log('[TasksPage] Found task:', task.name);
        setSelectedTask(task);
        setSelectedHistoryItem(null);
      } else {
        console.warn('[TasksPage] Task not found:', taskId);
      }
    };

    window.addEventListener('select-task', handleSelectTask);
    return () => window.removeEventListener('select-task', handleSelectTask);
  }, []);

  // Auto-open a specific job result when directed from notification or modal
  useEffect(() => {
    if (!openJobId || historyLoading || allTaskHistory.length === 0) return;
    const item = allTaskHistory.find(h => h.analysis_id === openJobId || h.job_id === openJobId);
    if (item) {
      setSelectedTask(null);
      setSelectedHistoryItem(item);
      onOpenJobIdConsumed?.();
    }
  }, [openJobId, historyLoading, allTaskHistory]);

  // Auto-select a task when directed (e.g. "View History" from Documents page)
  useEffect(() => {
    if (!openTaskId) return;
    const task = AVAILABLE_TASKS.find(t => t.id === openTaskId);
    if (task) {
      setSelectedTask(task);
      setSelectedHistoryItem(null);
      onOpenTaskIdConsumed?.();
    }
  }, [openTaskId]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    const categoryTasks = selectedCategory === 'all'
      ? categorizedTasks.all.tasks
      : categorizedTasks[selectedCategory]?.tasks || [];

    // Filter out hidden and coming soon tasks
    const visibleTasks = categoryTasks.filter(task => !task.hidden && !task.coming_soon);

    if (!searchTerm) return visibleTasks;

    return visibleTasks.filter(task =>
      task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categorizedTasks, selectedCategory, searchTerm]);

  const getDocumentIdFromResult = (taskResult: any, fallbackId?: string): string | undefined => {
    if (!taskResult) return fallbackId;

    const candidateFields = [
      'document_id',
      'doc_id',
      'docId',
      'documentId',
      'doc_no',
      'docNo',
      'selected_number',
      'selectedNumber',
      'selected_doc_id',
      'selectedDocId',
      'selected_document',
      'selectedDocument',
      'document',
      'doc',
      'application_number',
      'applicationNumber',
      'resource_id',
      'resourceId',
    ];

    const extractFrom = (source: any): string | undefined => {
      if (!source) return undefined;
      for (const field of candidateFields) {
        const value = source[field];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
        if (typeof value === 'number') {
          return value.toString();
        }
      }
      return undefined;
    };

    return extractFrom(taskResult) ||
           extractFrom(taskResult?.metadata) ||
           (fallbackId ? fallbackId.trim() : undefined);
  };

  // Handle adding task result to chat
  const handleAddToChat = (taskResult: any, taskName: string, taskType: string, timestamp: string) => {
    // Extract and format the content
    const resultData = getParsedResult(getResultData(taskResult));
    const contentString = typeof resultData === 'string'
      ? resultData
      : JSON.stringify(resultData, null, 2);
    const documentId = getDocumentIdFromResult(taskResult);

    // Create task attachment
    const attachment: TaskResultAttachment = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      taskName: taskName,
      taskType: taskType,
      timestamp: timestamp,
      content: contentString,
      documentId,
    };

    // Add to global store
    addTaskAttachment(attachment);

    // Show notification
    addNotification({
      type: 'success',
      title: 'Added to Chat',
      message: `${taskName} result has been added to your chat input.`,
      duration: 3000,
    });
  };

  // Helper function to format timestamps correctly in local time
  const formatTimestamp = (timestamp: string | Date | undefined): string => {
    if (!timestamp) return 'Unknown time';

    try {
      let dateStr = timestamp;

      // If timestamp is a string, check if it has a timezone indicator
      if (typeof dateStr === 'string') {
        // Check if timestamp has timezone indicator (Z, +HH:MM, or -HH:MM)
        const hasTimezone = /[Z]$|[+-]\d{2}:\d{2}$/.test(dateStr);

        // If no timezone indicator, assume UTC and add 'Z'
        if (!hasTimezone && dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
          // Remove any decimal seconds first
          const baseTimestamp = dateStr.split('.')[0];
          dateStr = baseTimestamp + 'Z';
        }
        // If timestamp has microseconds, truncate to milliseconds
        else if (dateStr.includes('.')) {
          const parts = dateStr.split('.');
          if (parts.length === 2) {
            const fractionalPart = parts[1];
            // Check if there's more than 3 digits before timezone indicator
            const timezoneMatch = fractionalPart.match(/([+-]\d{2}:\d{2}|Z)$/);
            if (timezoneMatch) {
              const timezone = timezoneMatch[0];
              const digits = fractionalPart.substring(0, fractionalPart.indexOf(timezone));
              if (digits.length > 3) {
                // Truncate to 3 digits (milliseconds)
                dateStr = parts[0] + '.' + digits.substring(0, 3) + timezone;
              }
            }
          }
        }
      }

      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return String(timestamp);
    }
  };

  // Detect if a history item is a multi-doc analysis
  // Primary signal: task_type; fallback: multiple resource_ids (legacy items stored as custom_analysis)
  const isMultiDocItem = (item: any): boolean =>
    item?.task_type === 'multi_doc_analysis' ||
    (Array.isArray(item?.resource_ids) && item.resource_ids.length > 1);

  // Extract result data from result object, checking multiple possible properties
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;

    // Check multiple possible property names where the actual data might be stored
    // Priority: result > output > analysis > use the object itself
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;

    // If none of the above exist, assume the object itself is the data
    return resultObj;
  };

  // Parse result output - it comes as a string that may need parsing
  const getParsedResult = (output: any) => {
    if (!output) return null;

    // If it's already an object, return it
    if (typeof output === 'object') return output;

    // If it's a string, try to parse it as JSON
    if (typeof output === 'string') {
      // Check if it looks like a Python dictionary (starts with { and contains single quotes)
      if (!output.trim().startsWith('{') || !output.includes("'")) {
        return output;
      }

      try {
        // Try to parse as JSON first
        return JSON.parse(output);
      } catch {
        // If parsing fails, try to evaluate as Python dict string
        try {
          // More robust Python to JSON conversion
          // Handle single quotes more carefully to avoid breaking contractions
          let jsonStr = output;

          // Replace single quotes only in structural positions (keys, values, arrays)
          jsonStr = jsonStr.replace(/'([^']*)':/g, '"$1":');  // Keys
          jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');  // Values after colons
          jsonStr = jsonStr.replace(/\[\s*'([^']*)'/g, '["$1"');  // Array first item
          jsonStr = jsonStr.replace(/,\s*'([^']*)'/g, ', "$1"');  // Array other items
          jsonStr = jsonStr.replace(/'([^']*)']/g, '"$1"]');  // Array last item

          // Handle Python boolean and null values
          jsonStr = jsonStr.replace(/True/g, 'true');
          jsonStr = jsonStr.replace(/False/g, 'false');
          jsonStr = jsonStr.replace(/None/g, 'null');

          return JSON.parse(jsonStr);
        } catch (e) {
          console.warn('Failed to parse Python dict:', output.substring(0, 100) + '...', 'Error:', e);
          // If all parsing fails, return the string as-is
          return output;
        }
      }
    }

    return output;
  };

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden">
      {/* Collapsed: a rail to bring the list back. The list is a picker — once a task
          is chosen it is the least useful thing on screen, and on a laptop its fixed
          320px is taken from the work itself. */}
      {!showTaskList && (
        <div className="w-10 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col items-center py-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setShowTaskList(true)}
            title="Show the task list"
            aria-label="Show the task list"
            aria-expanded={false}
          >
            <PanelLeftOpen className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Left: Filters & compact list */}
      {showTaskList && (
        <div className="w-72 xl:w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
          <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">Tasks</h2>
                <p className="text-xs text-gray-600 mt-1">
                  Filter and pick a workflow
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTaskInfoModal(true)}
                  className="h-7 w-7 p-0 text-xs font-semibold text-gray-600 border-gray-300 hover:bg-gray-100"
                  title="How tasks work"
                >
                  ?
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setShowTaskList(false)}
                  title="Hide the task list"
                  aria-label="Hide the task list"
                  aria-expanded={true}
                >
                  <PanelLeftClose className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tasks..."
                className="pl-9 h-8 text-sm"
              />
            </div>
          </div>

          <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full h-9">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categorizedTasks).map(([categoryKey, category]) => {
                  const CategoryIcon = TASK_CATEGORY_INFO[categoryKey]?.icon;
                  return (
                    <SelectItem key={categoryKey} value={categoryKey}>
                      <div className="flex items-center gap-2">
                        {CategoryIcon && <CategoryIcon className="w-4 h-4" />}
                        <span>{category.name}</span>
                        <Badge variant="secondary" className="ml-1 h-4 text-xs px-1">
                          {category.tasks.length}
                        </Badge>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1 overflow-y-auto max-h-[calc(100vh-240px)]">
              {filteredTasks.map((task) => {
                const TaskIcon = task.icon;
                const isSelected = selectedTask?.id === task.id;

                return (
                  <div key={task.id} className="relative">
                  <button
                    onClick={() => {
                      if (task.coming_soon) return;

                      if (task.premium_only && accountInfo?.account_type !== 'premium') {
                        addNotification({
                          type: 'warning',
                          title: 'Premium Feature',
                          message: `${task.name} is only available for Premium subscribers. Upgrade to access this feature.`,
                          duration: 5000,
                        });
                        return;
                      }

                      setSelectedTask(task);
                      setSelectedHistoryItem(null);
                    }}
                    disabled={task.coming_soon}
                    className={`w-full text-left p-3 pr-10 rounded-lg transition-all ${
                      isSelected
                        ? 'bg-blue-50 border border-blue-200'
                        : task.coming_soon
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {TaskIcon && (
                        <TaskIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {task.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {task.description}
                        </div>
                        {task.coming_soon && (
                          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200 mt-1 text-xs">
                            Coming Soon
                          </Badge>
                        )}
                        {task.premium_only && accountInfo?.account_type !== 'premium' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 mt-1 text-xs">
                            <Crown className="w-3 h-3 mr-1 inline" />
                            Premium
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                  <Button variant="ghost" size="icon" className="absolute right-1 top-2 h-8 w-8" aria-label={`${favoriteTaskIds.includes(task.id) ? 'Unfavorite' : 'Favorite'} ${task.name}`} aria-pressed={favoriteTaskIds.includes(task.id)} onClick={() => toggleFavorite(task.id)}><Star className={`h-4 w-4 ${favoriteTaskIds.includes(task.id) ? 'fill-amber-400 text-amber-500' : 'text-slate-400'}`} /></Button>
                  </div>
                );
              })}

              {filteredTasks.length === 0 && (
                <div className="text-center py-8 px-4">
                  <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No tasks found</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Right: Task Runner and Results */}
      <div className="flex-1 flex overflow-hidden">
        {selectedTask?.id === 'multi_doc_analysis' ? (
          // Side by side when there is width for it, stacked when there is not: below
          // `xl` a 420px form and a results pane leave neither enough room to read.
          <div className="flex-1 flex flex-col xl:flex-row overflow-hidden">
            {/* Left: input form */}
            <div className="min-h-0 flex-1 xl:flex-none w-full xl:w-[420px] xl:shrink-0 border-b xl:border-b-0 xl:border-r border-gray-200 overflow-y-auto bg-white flex flex-col">
              <div className="px-6 pt-5 pb-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquarePlus className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Multi-Doc Q&A</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedTask(null); setSelectedHistoryItem(null); }}>✕</Button>
              </div>
              <MultiDocQATaskPanel onJobComplete={() => setHistoryReloadKey(k => k + 1)} />
            </div>
            {/* Right: history for multi_doc_analysis */}
            <div className="flex-1 flex flex-col bg-white overflow-hidden">
              {selectedHistoryItem ? (
                <div className="flex-1 overflow-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-lg text-gray-900">Task Result</h3>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setSelectedHistoryItem(null)}>← Back to history</Button>
                    </div>
                    {(() => {
                      const parsedData = getParsedResult(getResultData(selectedHistoryItem));
                      return <MultiDocAnalysisDisplay result={parsedData} historyItem={selectedHistoryItem} showHeader={false} showToggle={true} />;
                    })()}
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-auto p-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">History</h3>
                  {historyLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
                  ) : allTaskHistory.filter(isMultiDocItem).length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No Multi-Doc Q&A results yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {allTaskHistory.filter(isMultiDocItem).map((item, index) => (
                        <button key={item.analysis_id || index} onClick={() => setSelectedHistoryItem(item)}
                          className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-all">
                          <div className="flex items-center gap-2 mb-1 min-w-0">
                            <MessageSquarePlus className="w-4 h-4 text-blue-500 shrink-0" />
                            <span className="text-sm font-medium text-gray-900 truncate" title={item.user_prompt || 'Multi-Doc Analysis'}>{item.user_prompt || 'Multi-Doc Analysis'}</span>
                          </div>
                          <div className="text-xs text-gray-500">{formatTimestamp(item.time_received || item.timestamp)}</div>
                          {item.resource_ids?.length > 0 && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate" title={item.resource_ids.join(', ')}>{item.resource_ids.length} documents</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : selectedTask ? (
          <UniversalTaskPanel
            task={selectedTask}
            preferredDocument={preferredDocument}
            onClose={() => setSelectedTask(null)}
          />
        ) : (
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1 flex flex-col bg-white overflow-hidden">
              {selectedHistoryItem ? (
                <div className="flex-1 overflow-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-lg text-gray-900">Task Result</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddToChat(
                            selectedHistoryItem,
                            AVAILABLE_TASKS.find(t => t.id === selectedHistoryItem.task_type)?.name || 'Task Result',
                            selectedHistoryItem.task_type,
                            selectedHistoryItem.time_received || selectedHistoryItem.timestamp
                          )}
                          className="gap-2"
                        >
                          <MessageSquarePlus className="w-4 h-4" />
                          Add to Chat
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedHistoryItem(null)}
                        >
                          Back
                        </Button>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-white">
                          {isMultiDocItem(selectedHistoryItem)
                            ? 'Multi-Doc Analysis'
                            : AVAILABLE_TASKS.find(t => t.id === selectedHistoryItem.task_type)?.name || selectedHistoryItem.task_type || 'Unknown Task'}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(selectedHistoryItem.time_received || selectedHistoryItem.timestamp)}
                        </span>
                      </div>
                      {isMultiDocItem(selectedHistoryItem) ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedHistoryItem.resource_ids.map((id: string, i: number) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {id.startsWith('patent-') ? `US Patent ${id.slice(7)}`
                                : id.startsWith('publication-') ? `US Pub ${id.slice(12)}`
                                : id.startsWith('foreign_patent-') ? id.slice(15)
                                : id}
                            </Badge>
                          ))}
                        </div>
                      ) : selectedHistoryItem.selected_number && (
                        <p className="text-xs text-gray-600">
                          Document: {selectedHistoryItem.selected_number}
                        </p>
                      )}
                      {selectedHistoryItem.credits_used !== undefined && (
                        <p className="text-xs text-gray-600 mt-1">
                          Credits used: {selectedHistoryItem.credits_used}
                        </p>
                      )}
                    </div>

                    {(() => {
                      const parsedData = getParsedResult(getResultData(selectedHistoryItem));
                      const commonProps = {
                      result: parsedData,
                      theme: 'light' as const,
                      showHeader: true,
                      showToast,
                      onCopyResult: () => copyResult(parsedData, selectedHistoryItem.task_type),
                    };

                      switch (selectedHistoryItem.task_type) {
                        case 'parts_list':
                          return <PartsListDisplay {...commonProps} showToggle={true} />;
                        case 'spec_map':
                          return <SpecMapDisplay {...commonProps} showToggle={true} />;
                        case 'claims_support':
                          return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
                        case 'claim_chart':
                          return <ClaimChartDisplay {...commonProps} />;
                        case 'claims_with_numerals':
                          return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
                        case 'multi_doc_analysis':
                          return <MultiDocAnalysisDisplay {...commonProps} historyItem={selectedHistoryItem} showToggle={true} />;
                        case 'custom_analysis':
                          if (isMultiDocItem(selectedHistoryItem)) {
                            return <MultiDocAnalysisDisplay {...commonProps} historyItem={selectedHistoryItem} showToggle={true} />;
                          }
                          return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
                        case 'ir_metadata':
                          return <IRMetadataDisplay {...commonProps} />;
                        case 'ir_innovation':
                          return <IRInnovationAnalysisDisplay {...commonProps} />;
                        case 'ir_tech_class':
                          return <IRTechClassificationDisplay {...commonProps} />;
                        case 'draft_summary':
                          return <DraftSummaryDisplay {...commonProps} />;
                        case 'draft_detailed_description':
                          return <DraftDetailedDescriptionDisplay {...commonProps} />;
                        case 'draft_background':
                          return <DraftBackgroundDisplay {...commonProps} />;
                        case 'draft_claims':
                          return <DraftClaimsDisplay {...commonProps} />;
                        case 'draft_other_embodiments':
                          return <OtherEmbodimentsDisplay {...commonProps} />;
                        case 'oa_extract':
                          return <OAExtractDisplay {...commonProps} />;
                        case 'oa_analyze':
                          return <OAAnalyzeDisplay {...commonProps} />;
                        case 'oa_summary_art':
                          return <OASummaryArtDisplay {...commonProps} />;
                        case 'oa_timeline':
                          return <OATimelineDisplay {...commonProps} />;
                        case 'translate_document':
                          return <TranslateDocumentDisplay {...commonProps} />;
                        case 'latex_to_docx':
                          return <LaTeXConvertDisplay {...commonProps} />;
                        case 'ocr_pdf':
                          return <OCRPdfDisplay {...commonProps} />;
                        case 'antecedent_basis_check':
                          return <AntecedentBasisDisplay {...commonProps} />;
                        case 'technical_outline':
                          return <TechnicalOutlineDisplay {...commonProps} />;
                        case 'parts_process_outline':
                          return <PartsProcessOutlineDisplay {...commonProps} />;
                        case 'oa_reporting_email':
                          return <OAEmailResultView data={parsedData} />;
                        default:
                          return (
                            <div className="p-4 rounded-lg bg-gray-50">
                              <pre className="text-xs overflow-auto whitespace-pre-wrap">
                                {JSON.stringify(parsedData, null, 2)}
                              </pre>
                            </div>
                          );
                      }
                    })()}
                  </div>
                </div>
              ) : (
                <TaskShortcuts
                  tasks={categorizedTasks.all.tasks}
                  favoriteIds={favoriteTaskIds}
                  recentIds={[...recentTaskIds, ...allTaskHistory.map(item => item.task_type).filter(Boolean)]}
                  isPremium={accountInfo?.account_type === 'premium'}
                  onSelect={task => { setSelectedTask(task); setSelectedHistoryItem(null); }}
                  onToggleFavorite={toggleFavorite}
                  onBrowse={() => setShowTaskList(true)}
                />
              )}
            </div>

            {/* History sidepane */}
            <div className="w-72 min-w-[14rem] max-w-[18rem] border-l border-gray-200 bg-slate-50/80 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Task History</h3>
                  <p className="text-xs text-gray-500">Newest first</p>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  {allTaskHistory.length}
                </Badge>
              </div>
              <div className="flex-1 overflow-auto p-3">
                {historyLoading ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin mb-2" />
                    <p className="text-xs text-gray-500">Loading history...</p>
                  </div>
                ) : allTaskHistory.length === 0 ? (
                  <div className="text-center text-xs text-gray-500 py-8">
                    No task runs yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {allTaskHistory.map((item, index) => {
                      const multiDoc = isMultiDocItem(item);
                      const taskInfo = multiDoc ? null : AVAILABLE_TASKS.find(t => t.task_type === item.task_type || t.id === item.task_type);
                      const TaskIcon = multiDoc ? MessageSquarePlus : taskInfo?.icon;
                      const displayName = multiDoc ? 'Multi-Doc Q&A' : (taskInfo?.name || item.task_type || 'Task');
                      const subTitle = multiDoc
                        ? (item.user_prompt || (item.resource_ids?.length ? `${item.resource_ids.length} documents` : ''))
                        : item.selected_number ? `Doc: ${item.selected_number}` : '';
                      return (
                        <button
                          key={item.analysis_id || index}
                          onClick={() => setSelectedHistoryItem(item)}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            selectedHistoryItem === item
                              ? 'border-blue-300 bg-white shadow-sm'
                              : 'border-transparent bg-white/80 hover:border-blue-200 hover:bg-white'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {TaskIcon && (
                              <TaskIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${multiDoc ? 'text-blue-500' : 'text-gray-400'}`} />
                            )}
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <p className="text-sm font-medium text-gray-900 truncate" title={displayName}>
                                {displayName}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {formatTimestamp(item.time_received || item.timestamp)}
                              </p>
                              {subTitle && (
                                <p className="text-[11px] text-gray-500 truncate" title={subTitle}>
                                  {subTitle}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showTaskInfoModal} onOpenChange={setShowTaskInfoModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">How Tasks Work</DialogTitle>
            <DialogDescription className="text-gray-600">
              Tasks are guided workflows that package proven prompts and output layouts so you can generate consistent patent
              deliverables with a few clicks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-600">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">What happens when you run a task</h4>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Pick a task and fill in any requested inputs (claims, doc selections, focus instructions).</li>
                <li>After submission, a task result is generated that you can view or insert into chat for further analysis.</li>
              </ol>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Tips</h4>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Use the search and category filter to quickly find the workflow you need.</li>
                <li>Task history keeps every run—open a result to review details or push it back into the chat.</li>
                <li>Select the documents/resources or insert text into the task input and then select submit to run the task.</li>
                <li>Tasks incur credits based on the size and complexity of the task.</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
